import { GazinProviderUI } from '@gazin/ecommerce-ui'
import * as Sentry from '@sentry/nextjs'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { CartContextProvider } from 'src/contexto/cartContext'
import { useBlackFriday } from 'src/contexto/hooks/useBlackFriday'
import { FavoriteProvider } from 'src/contexto/hooks/useFavorite'
import { apiV2 } from 'src/services/api'
import { setPreviousRoute } from 'src/util/route'
import { ThemeProvider } from 'styled-components'
import Carregando from '../components/carregando'
import DatadogBrowserRum from '../components/datadog/browser-rum'
import facebookActions from '../components/facebookPixel/facebookActions'
import ContextoGlobalProvider from '../contexto/contextoGlobal'
import { useLivelo } from '../contexto/hooks/useLivelo'
import { inter } from '../fonts'
import * as fbq from '../services/fbq'
import * as gtg from '../services/gtag'
import * as gtm from '../services/gtm'
import EstiloGlobal from '../styles'
import { theme } from '../styles/themes/default'
import { getNonce } from '../util/csp'
import { Campaign } from '../util/enums/Campaign'
import useLinxMeta from '../util/hooks/useLinxMeta'
import { useInitialData } from 'src/contexto/hooks/useInitialData'

const ToastContainer = dynamic(
  () => import('react-toastify').then((module) => module.ToastContainer),
  { ssr: false }
)

export const RouterEventHandler = ({ start, end }) => {
  const router = useRouter()
  const { routeChangeLinx } = useLinxMeta()
  const { createCookieLivelo, updateCookieLivelo, updateLiveloParityData } =
    useLivelo()

  useEffect(() => {
    const pathTokens = router.pathname?.substring(1)?.split('/')
    const isLiveloPage =
      pathTokens?.length === 1 && pathTokens[0] === Campaign.LIVELO

    isLiveloPage ? createCookieLivelo() : updateCookieLivelo()
  }, [router])

  useEffect(() => {
    updateLiveloParityData()
  }, [router.pathname])

  const handleRouteChangeComplete = useCallback((url) => {
    try {
      gtm.pageView()
      gtg.pageView()
      routeChangeLinx(url)
      facebookActions.pageView()
    } catch (err) {
      Sentry.captureException(err)
    } finally {
      end()
    }
  }, [])

  const handleRouteChangeStart = useCallback(() => {
    setPreviousRoute(router.pathname)
    start()
  }, [router.pathname])

  useEffect(() => {
    handleRouteChangeComplete(router.pathname)
    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    router.events.on('hashChangeComplete', handleRouteChangeComplete)
    router.events.on('routeChangeError', end)
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
      router.events.off('hashChangeComplete', handleRouteChangeComplete)
      router.events.off('routeChangeError', end)
    }
  }, [router.events, router.pathname, handleRouteChangeComplete])
}

export default function App({
  Component,
  pageProps,
  err,
  nonce,
  paymentMethods,
  menusData,
  bannerHeaderData,
  sacInformation,
}) {
  const [loading, setLoading] = useState(false)
  const { initLinxMeta } = useLinxMeta()
  const { isBlackFridayTheme } = useBlackFriday()

  const start = useCallback(() => {
    setLoading(true)
  }, [])

  const end = useCallback(() => {
    setLoading(false)
  }, [])

  useEffect(() => {
    initLinxMeta()
  })

  return (
    <>
      <style jsx global>
        {`
          html {
            font-family: ${inter.style.fontFamily};
          }
        `}
      </style>
      {
        /* Google Tag Manager */
        gtm.renderSnippet(nonce)
      }
      {
        /* Google Analytics */
        gtg.renderSnippet(nonce)
      }
      {
        /* Facebook Pixel */
        fbq.renderSnippet(nonce)
      }
      {loading && <Carregando />}
      <ContextoGlobalProvider
        start={start}
        end={end}
        nonce={nonce}
        isBlackFridayTheme={isBlackFridayTheme}
        paymentMethods={paymentMethods}
        menusData={menusData}
        bannerHeaderData={bannerHeaderData}
        sacInformation={sacInformation}
      >
        <CartContextProvider>
          <ThemeProvider theme={theme}>
            <EstiloGlobal />
            <ToastContainer
              autoClose={3000}
              draggable={false}
              limit={1}
              icon={false}
              theme="colored"
            />
            <GazinProviderUI resetCSS={false}>
              <FavoriteProvider>
                <RouterEventHandler start={start} end={end} />
                <DatadogBrowserRum />
                <Component {...pageProps} err={err} />
              </FavoriteProvider>
            </GazinProviderUI>
          </ThemeProvider>
        </CartContextProvider>
      </ContextoGlobalProvider>
    </>
  )
}

App.getInitialProps = async ({ ctx }) => {
  const nonce = getNonce(ctx)
  const initialData = await useInitialData()

  return {
    nonce,
    ...initialData,
  }
}
